import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import type { InteractiveElement } from '~/stores/interactiveElements.store';
import { weatherStore } from '~/stores/weather.store';
import { weatherIconDataUri } from './weatherIcons';
import {
  resolveWeatherStyle,
  resolveWeatherTheme,
  weatherBackground,
  localDay,
  localTime,
  localDate,
  type WeatherStyleKey,
} from './weatherStyles';

interface Props {
  element: InteractiveElement;
}

const Wrapper = styled.div<{ $x: number; $y: number; $w: number; $h: number }>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  width: ${({ $w }) => $w}%;
  height: ${({ $h }) => $h}%;
  pointer-events: none; /* display-only */
`;

const Card = styled.div<{ $bg: string }>`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 14px;
  background: ${({ $bg }) => $bg};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
`;

const Img = styled.img`
  display: block;
  flex-shrink: 0;
  object-fit: contain;
`;

interface ViewModel {
  boxW: number;
  boxH: number;
  fg: string;
  temp: string;
  city: string;
  uf: string;
  cond: string;
  day: string;
  date: string;
  time: string;
  iconUri: string;
}

/** font-size em px proporcional à altura da caixa (com mínimo legível). */
function fpx(boxH: number, frac: number, min = 8): string {
  return `${Math.max(min, Math.round(boxH * frac))}px`;
}

const ellipsis: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
};

// Container flex padrão de um card. minWidth:0 nos filhos garante truncamento
// (ellipsis) em vez de transbordar e sobrepor o vizinho.
const row = (boxH: number, padFrac: number): React.CSSProperties => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  padding: fpx(boxH, padFrac, 5),
  gap: fpx(boxH, 0.06, 4),
});

function Detailed(vm: ViewModel) {
  const { boxH, fg } = vm;
  return (
    <div
      style={{ ...row(boxH, 0.08), justifyContent: 'space-between', color: fg }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{ fontSize: fpx(boxH, 0.115), fontWeight: 600, ...ellipsis }}
        >
          {vm.city}
        </div>
        <div style={{ fontSize: fpx(boxH, 0.11), opacity: 0.9, ...ellipsis }}>
          {vm.day} · {vm.time}
        </div>
        {vm.cond && (
          <div
            style={{ fontSize: fpx(boxH, 0.11), opacity: 0.85, ...ellipsis }}
          >
            {vm.cond}
          </div>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: fpx(boxH, 0.04, 3),
          flexShrink: 0,
        }}
      >
        <Img
          src={vm.iconUri}
          alt=""
          style={{ width: fpx(boxH, 0.42), height: fpx(boxH, 0.42) }}
        />
        <div
          style={{ fontSize: fpx(boxH, 0.34), fontWeight: 700, lineHeight: 1 }}
        >
          {vm.temp}
        </div>
      </div>
    </div>
  );
}

function Banner(vm: ViewModel) {
  const { boxH, fg } = vm;
  return (
    <div
      style={{ ...row(boxH, 0.1), justifyContent: 'space-between', color: fg }}
    >
      <div style={{ minWidth: 0, overflow: 'hidden' }}>
        {vm.cond && (
          <div
            style={{ fontSize: fpx(boxH, 0.16), fontWeight: 600, ...ellipsis }}
          >
            {vm.cond}
          </div>
        )}
        <div
          style={{ fontSize: fpx(boxH, 0.44), fontWeight: 700, lineHeight: 1 }}
        >
          {vm.temp}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0, textAlign: 'center', opacity: 0.92 }}>
        <div
          style={{ fontSize: fpx(boxH, 0.18), fontWeight: 600, ...ellipsis }}
        >
          {vm.day}
        </div>
        <div style={{ fontSize: fpx(boxH, 0.12), ...ellipsis }}>{vm.city}</div>
        <div style={{ fontSize: fpx(boxH, 0.13), opacity: 0.85, ...ellipsis }}>
          {vm.time}
        </div>
      </div>
      <Img
        src={vm.iconUri}
        alt=""
        style={{ width: fpx(boxH, 0.8), height: fpx(boxH, 0.8), flexShrink: 0 }}
      />
    </div>
  );
}

function Vertical(vm: ViewModel) {
  const { boxH, fg } = vm;
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-evenly',
        padding: fpx(boxH, 0.04, 5),
        boxSizing: 'border-box',
        color: fg,
      }}
    >
      {vm.cond && (
        <div
          style={{
            fontSize: fpx(boxH, 0.062),
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            opacity: 0.95,
            ...ellipsis,
          }}
        >
          {vm.cond}
        </div>
      )}
      <Img
        src={vm.iconUri}
        alt=""
        style={{ width: fpx(boxH, 0.26), height: fpx(boxH, 0.26) }}
      />
      <div style={{ fontSize: fpx(boxH, 0.058), fontWeight: 600, ...ellipsis }}>
        {vm.city}
      </div>
      <div style={{ fontSize: fpx(boxH, 0.05), opacity: 0.85, ...ellipsis }}>
        {vm.day} · {vm.time}
      </div>
      <div
        style={{ fontSize: fpx(boxH, 0.16), fontWeight: 700, lineHeight: 1 }}
      >
        {vm.temp}
      </div>
    </div>
  );
}

function Badge(vm: ViewModel) {
  const { boxH, fg } = vm;
  // Coluna única de texto ao lado do ícone — sem colunas que se cruzam.
  return (
    <div style={{ ...row(boxH, 0.1), color: fg }}>
      <Img
        src={vm.iconUri}
        alt=""
        style={{
          width: fpx(boxH, 0.55),
          height: fpx(boxH, 0.55),
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <div
          style={{
            fontSize: fpx(boxH, 0.36),
            fontWeight: 700,
            lineHeight: 1.05,
            ...ellipsis,
          }}
        >
          {vm.temp}
        </div>
        <div
          style={{ fontSize: fpx(boxH, 0.13), fontWeight: 600, ...ellipsis }}
        >
          {vm.city}
        </div>
        <div style={{ fontSize: fpx(boxH, 0.12), opacity: 0.8, ...ellipsis }}>
          {vm.cond ? `${vm.cond} · ${vm.day}` : vm.day}
        </div>
      </div>
    </div>
  );
}

// Barra horizontal (tira): clima à esquerda, localização + data/hora à direita.
function Bar(vm: ViewModel) {
  const { boxH, fg } = vm;
  const location = [vm.city, vm.uf].filter(Boolean).join(' ');
  return (
    <div
      style={{ ...row(boxH, 0.12), justifyContent: 'space-between', color: fg }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: fpx(boxH, 0.1, 6),
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        <Img
          src={vm.iconUri}
          alt=""
          style={{ width: fpx(boxH, 0.62), height: fpx(boxH, 0.62) }}
        />
        <div
          style={{ fontSize: fpx(boxH, 0.44), fontWeight: 700, lineHeight: 1 }}
        >
          {vm.temp}
        </div>
        {vm.cond && (
          <div style={{ fontSize: fpx(boxH, 0.26), opacity: 0.9, ...ellipsis }}>
            {vm.cond}
          </div>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: fpx(boxH, 0.14, 8),
          fontSize: fpx(boxH, 0.26),
          minWidth: 0,
        }}
      >
        {location && (
          <span style={{ fontWeight: 600, ...ellipsis }}>{location}</span>
        )}
        <span style={{ opacity: 0.9, whiteSpace: 'nowrap' }}>
          {vm.day} {vm.date}
        </span>
        <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{vm.time}</span>
      </div>
    </div>
  );
}

const LAYOUTS: Record<WeatherStyleKey, (vm: ViewModel) => React.ReactElement> =
  {
    detailed: Detailed,
    banner: Banner,
    vertical: Vertical,
    badge: Badge,
    bar: Bar,
  };

/**
 * Widget de clima (display-only). Placement vem do elemento (x/y/w/h, opacidade,
 * cores); o DADO vem do weatherStore (sync). Some gracioso se não há clima.
 * Estilo (detailed/vertical/banner/badge) + tema vêm de element.actionPayload.
 * Renderiza em todas as plataformas (DOM puro + ícone SVG).
 */
const InteractiveWeather: React.FC<Props> = observer(({ element }) => {
  const [isActive, setIsActive] = useState(element.inSeconds <= 0);
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    const onResize = () =>
      setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const timers: number[] = [];
    if (element.inSeconds > 0) {
      timers.push(
        window.setTimeout(() => setIsActive(true), element.inSeconds * 1000),
      );
    }
    if (element.outSeconds !== null && element.outSeconds > 0) {
      timers.push(
        window.setTimeout(() => setIsActive(false), element.outSeconds * 1000),
      );
    }
    return () => timers.forEach(clearTimeout);
  }, [element.inSeconds, element.outSeconds]);

  const w = weatherStore.current;
  if (!w) return null; // sem clima → esconde gracioso

  const styleKey = resolveWeatherStyle(element.actionPayload);
  const theme = resolveWeatherTheme(element.actionPayload);
  const bg = weatherBackground(theme, element.bgColor);

  const vm: ViewModel = {
    boxW: (element.wPct / 100) * vp.w,
    boxH: (element.hPct / 100) * vp.h,
    fg: element.textColor || '#FFFFFF',
    temp: w.temp !== null ? `${Math.round(w.temp)}°` : '--°',
    city: w.city || w.refCity || '',
    uf: w.uf || '',
    cond: w.conditionPt || '',
    day: localDay(now),
    date: localDate(now),
    time: localTime(now),
    iconUri: weatherIconDataUri(w.icon),
  };

  const Layout = LAYOUTS[styleKey];

  return (
    <Wrapper
      $x={element.xPct}
      $y={element.yPct}
      $w={element.wPct}
      $h={element.hPct}
      style={{ opacity: isActive ? element.opacity : 0 }}
    >
      <Card $bg={bg}>{Layout(vm)}</Card>
    </Wrapper>
  );
});

export default InteractiveWeather;
